import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { apiUrl } from "../helper";
import { STANDS, STAND_ORDER } from "../stands";
import { expandArticleToStandLines } from "../articleKit";
import "../styles/InvoicePage.scss";
import { resolveImageUrl } from "../imageUrl";

const parseInvoiceData = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
        try {
            return JSON.parse(raw);
        } catch (_error) {
            return [];
        }
    }
    return [];
};

const isArtisanOrTracteurLine = (line) => /artisan|tracteur/i.test(String(line?.name || ""));

/**
 * - Lignes artisan/tracteur : 1 ticket papier par unité (37 kits → 37×3 tickets)
 * - Autres articles : regroupés par stand (comportement caisse classique)
 */
const buildTickets = (rawItems) => {
    if (!Array.isArray(rawItems)) return [];

    const standBuckets = {};
    const individual = [];

    rawItems.forEach((article) => {
        expandArticleToStandLines(article).forEach((line) => {
            const stand = line.stand || "repas";
            const cleanName = String(line.name || "")
                .replace(/^(\d+)\s*×\s*/i, "")
                .replace(/^1\s+/, "")
                .trim();

            if (isArtisanOrTracteurLine(line) || isArtisanOrTracteurLine({ name: cleanName })) {
                individual.push({
                    key: `${stand}-${individual.length}`,
                    stand,
                    label: STANDS[stand]?.label || stand,
                    lines: [{
                        name: cleanName,
                        qty: 1,
                        price: Number(line.price) || 0,
                        stand,
                    }],
                    total: Number(line.price) || 0,
                });
                return;
            }

            if (!standBuckets[stand]) standBuckets[stand] = [];
            standBuckets[stand].push({
                name: cleanName || line.name,
                qty: Math.max(1, Number(line.amount) || 1),
                price: Number(line.price) || 0,
                stand,
            });
        });
    });

    // Individuels dans l'ordre des stands (tous les boissons, puis repas, puis desserts)
    individual.sort((a, b) => {
        const ia = STAND_ORDER.indexOf(a.stand);
        const ib = STAND_ORDER.indexOf(b.stand);
        const sa = ia === -1 ? 99 : ia;
        const sb = ib === -1 ? 99 : ib;
        if (sa !== sb) return sa - sb;
        return String(a.key).localeCompare(String(b.key));
    });

    const grouped = [
        ...STAND_ORDER.filter((stand) => standBuckets[stand]?.length),
        ...Object.keys(standBuckets).filter((stand) => !STAND_ORDER.includes(stand)),
    ].map((stand) => {
        // fusion qty pour articles normaux uniquement
        const map = new Map();
        standBuckets[stand].forEach((line) => {
            const key = line.name;
            const prev = map.get(key);
            if (prev) {
                prev.qty += line.qty;
                prev.price += line.price;
            } else {
                map.set(key, { ...line });
            }
        });
        const lines = Array.from(map.values());
        return {
            key: `group-${stand}`,
            stand,
            label: STANDS[stand]?.label || stand,
            lines,
            total: lines.reduce((sum, line) => sum + (Number(line.price) || 0), 0),
        };
    });

    return [...individual, ...grouped];
};

const PRINT_GAP_MS = 1200;
const PRINT_FALLBACK_MS = 5000;

const InvoicePage = () => {
    const { id } = useParams();
    const [invoice, setInvoice] = useState(null);
    const [loadError, setLoadError] = useState("");
    const [printIndex, setPrintIndex] = useState(0);
    const [printingStarted, setPrintingStarted] = useState(false);
    const printLockRef = useRef(false);
    const doneRef = useRef(false);

    const tickets = useMemo(() => {
        if (!invoice?.data) return [];
        return buildTickets(invoice.data);
    }, [invoice]);

    useEffect(() => {
        let active = true;

        const fetchInvoice = async () => {
            try {
                const response = await axios.get(`${apiUrl}/api/invoice/${id}`, { timeout: 8000 });

                if (!active) return;
                if (response.data.status === "error" || !response.data.invoice) {
                    setLoadError("Ticket introuvable");
                    return;
                }

                const raw = parseInvoiceData(response.data.invoice.data);
                if (!raw.length) {
                    setLoadError("Ticket vide");
                    return;
                }

                setInvoice({
                    ...response.data.invoice,
                    data: raw,
                });
            } catch (_error) {
                if (active) {
                    setLoadError("Impossible de charger le ticket (hors ligne ?)");
                }
            }
        };

        fetchInvoice();
        return () => {
            active = false;
        };
    }, [id]);

    useEffect(() => {
        if (tickets.length > 0 && !printingStarted) {
            setPrintingStarted(true);
            setPrintIndex(0);
        }
    }, [tickets, printingStarted]);

    useEffect(() => {
        if (!printingStarted || printIndex >= tickets.length || doneRef.current) return undefined;
        if (printLockRef.current) return undefined;

        let cancelled = false;
        let fallbackTimer;

        const goNext = () => {
            if (cancelled || doneRef.current) return;
            printLockRef.current = false;
            window.onafterprint = null;
            if (printIndex < tickets.length - 1) {
                window.setTimeout(() => {
                    if (!cancelled && !doneRef.current) {
                        setPrintIndex((prev) => prev + 1);
                    }
                }, PRINT_GAP_MS);
                return;
            }
            doneRef.current = true;
            window.location.href = "/caisse";
        };

        const handleAfterPrint = () => {
            window.clearTimeout(fallbackTimer);
            goNext();
        };

        printLockRef.current = true;
        window.onafterprint = handleAfterPrint;

        const timer = window.setTimeout(() => {
            if (cancelled) return;
            try {
                window.print();
            } catch (_error) {
                goNext();
                return;
            }
            fallbackTimer = window.setTimeout(goNext, PRINT_FALLBACK_MS);
        }, 250);

        return () => {
            cancelled = true;
            printLockRef.current = false;
            window.clearTimeout(timer);
            window.clearTimeout(fallbackTimer);
            window.onafterprint = null;
        };
    }, [printIndex, printingStarted, tickets.length]);

    const invoiceDate = invoice?.updatedAt || invoice?.createdAt
        ? new Date(invoice.updatedAt || invoice.createdAt)
        : new Date();
    const logoUrl = resolveImageUrl("/assets/img/logo-foire.png");
    const currentTicket = tickets[printIndex];

    if (loadError) {
        return (
            <div className="invoice-page" style={{ padding: 24, textAlign: "center" }}>
                <p>{loadError}</p>
                <button type="button" className="btn btn-primary" onClick={() => { window.location.href = "/caisse"; }}>
                    Retour caisse
                </button>
            </div>
        );
    }

    if (!currentTicket) {
        return (
            <div className="invoice-page" style={{ padding: 24, textAlign: "center" }}>
                <p>Préparation de l&apos;impression…</p>
            </div>
        );
    }

    return (
        <div className="invoice-page">
            {/* Un seul ticket visible à la fois → une seule coupe imprimante */}
            <div className="ticket" key={currentTicket.key || `${currentTicket.stand}-${printIndex}`}>
                <div className="header">
                    <img className="ticket-logo" src={logoUrl} alt="Foire à l'Ail de Locon" />
                    <h5>Foire à L&apos;ail de Locon</h5>
                    <h6 className="ticket-stand">{currentTicket.label}</h6>
                    <p className="ticket-progress" style={{ fontSize: "12px", margin: 0 }}>
                        {printIndex + 1}/{tickets.length}
                    </p>
                    <p style={{ fontSize: "13px" }}>
                        {(new Intl.DateTimeFormat("fr-FR", {
                            weekday: "long",
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                        })).format(invoiceDate)}
                    </p>
                </div>
                <div className="container">
                    {currentTicket.lines.map((article, lineIndex) => (
                        <div key={`${currentTicket.key}-${lineIndex}`}>
                            <div className="quantity">{article.qty > 1 ? `${article.qty}×` : "1×"}</div>
                            <div className="article">{article.name}</div>
                            {Number(article.price) > 0 && (
                                <div className="price">{article.price}€</div>
                            )}
                        </div>
                    ))}
                </div>
                <div className="footer">
                    Total :
                    <span style={{ margin: "0 0 0 5px" }}>
                        <strong>{currentTicket.total.toFixed(2)}€</strong>
                    </span>
                </div>
            </div>
        </div>
    );
};

export default InvoicePage;
